import i18next from "i18next"
import { DepartmentPage, SpaarnePage, SubcentraPage, SubdepartmentPage } from "../root/cmsPage/cmsPageState"

type BreadcrumbItem = {
  title: string
  url: string
}

const buildBreadcrumbWithAbsolutePath = (
  absolutePath: string,
  labels: string[],
  lang: string = "nl"
): BreadcrumbItem[] => {
  const langPrefix = lang == "en" ? "/en" : "/"
  const pathWithoutLang = absolutePath.startsWith("/en") 
    ? absolutePath.slice(3)
    : absolutePath
  return pathWithoutLang.split("/").reduce(
    (acc, path, i) => {
      const fullPath = i == 0 
        ? acc.prefix
        : `${acc.prefix}${acc.prefix.endsWith("/") ? "" : "/"}${path}`
      return ({
        items: [...acc.items, {
          title: labels[i],
          url: fullPath
        }],
        prefix: fullPath
      })
    }, { 
      items: [] as BreadcrumbItem[], 
      prefix: langPrefix
    }
  ).items
}

// Per content type:
const SpaarneGasthuis = 'Spaarne Gasthuis'
const AfdelingenEnCentra = 'Afdelingen en Centra'

export const spaarneBreadcrumbs = (
  page: SpaarnePage
): BreadcrumbItem[] => {
  // Child of a Dossier page (/dossiers/{dossier}/{slug}): there is no /dossiers overview page,
  // so skip that segment, like the Dossier page's own breadcrumbs do
  if (page.parent != null && page.url.startsWith("/dossiers/")) {
    return [
      { title: SpaarneGasthuis, url: "/" },
      { title: page.parent.title, url: page.parent.url },
      { title: page.title, url: page.url },
    ]
  }

  const breadcrumbLabels = [
    SpaarneGasthuis
  ]

  if (page.parent != null) {
    if (page.parent.kind == "centrum") {
      breadcrumbLabels.push(AfdelingenEnCentra)
      if (page.parent.centrum) {
        breadcrumbLabels.push(page.parent.centrum.title)
      }
    } else {
      if (page.parent.parent) {
        breadcrumbLabels.push(page.parent.parent.title)
      }
    }
    breadcrumbLabels.push(page.parent.title)
  }

  breadcrumbLabels.push(page.title)

  return buildBreadcrumbWithAbsolutePath(page.url, breadcrumbLabels, i18next.language)
}

export const departmentBreadcrumbs = (
  page: DepartmentPage
): BreadcrumbItem[] => {
  const breadcrumbLabels = [
    SpaarneGasthuis,
    AfdelingenEnCentra
  ]

  if (page.department?.centrum) {
    breadcrumbLabels.push(page.department.centrum.title)
  }
  
  breadcrumbLabels.push(page.title)

  return buildBreadcrumbWithAbsolutePath(page.url, breadcrumbLabels)
}

export const subdepartmentBreadcrumbs = (
  page: SubdepartmentPage
): BreadcrumbItem[] => {
  const breadcrumbLabels = [
    SpaarneGasthuis,
    AfdelingenEnCentra
  ]
  
  if (page.subDepartment) {
    if (page.subDepartment?.department) {
      if (page.subDepartment.department.centrum) {
        breadcrumbLabels.push(page.subDepartment.department.centrum.title)
      }
      breadcrumbLabels.push(page.subDepartment.department.title )
    } else if (page.subDepartment?.centrum) {
      breadcrumbLabels.push(page.subDepartment.centrum.title)
    }
  }

  breadcrumbLabels.push(page.title)

  return buildBreadcrumbWithAbsolutePath(page.url, breadcrumbLabels)
}

export const subcentraBreadcrumbs = (
  page: SubcentraPage
): BreadcrumbItem[] => {
  const breadcrumbLabels = [
    SpaarneGasthuis,
    AfdelingenEnCentra
  ]
  
  if (page.subCentra?.centrum) {
    breadcrumbLabels.push(page.subCentra.centrum.title)
  }

  breadcrumbLabels.push(page.title)

  return buildBreadcrumbWithAbsolutePath(page.url, breadcrumbLabels)
}